import { Button, Popover, PopoverContent, PopoverTrigger } from '@cuspar/ui';
import { ShoppingCart } from 'lucide-react';
import { useCartStore } from '../../state/cartStore';
import { Link } from 'react-router';
import { CartBannerDomainItem } from './CartBannerDomainItem';
import { useOrderStore } from '../../state/orderStore';
import { useAuthStore } from '../../state/authStore';
import { useEffect, useState } from 'react';
import { ROUTES } from '../../routes';
import { useRouteName } from '../../hooks/useRouteName';
import { ICommonCart } from '@cuspar/ywt.ch.model';
import { CartBannerStaticHostingItem } from './CartBannerStaticHostingItem';
import { useTranslation } from 'react-i18next';
import { getUnpaidRenewalOrders } from '../../pages/Checkout/CheckoutCartPage.logic';

export const getCartItemsCount = (cart: ICommonCart) => {
  return cart.domains.length + (cart.staticHosting ? 1 : 0);
};

export const CartBanner = () => {
  const { t } = useTranslation();
  const cart = useCartStore((state) => state.cart);
  const orders = useOrderStore((state) => state.orders);
  const [showCart, setShowCart] = useState(false);

  const routeName = useRouteName();

  useEffect(() => {
    if (routeName === 'checkout.cart') setShowCart(false);
  }, [routeName]);

  const { user } = useAuthStore();
  const getMyOrders = useOrderStore((state) => state.getMyOrders);

  const unpaidRenewalOrders = getUnpaidRenewalOrders(orders);

  const itemsCount = cart ? getCartItemsCount(cart) : 0;

  function handleOpenCart(open: boolean) {
    if (open && user) getMyOrders();
    setShowCart(open);
  }

  if (!cart || itemsCount === 0) {
    return (
      <Popover open={showCart} onOpenChange={handleOpenCart}>
        <PopoverTrigger asChild>
          <Button className='px-2' variant={'ghost'} size={'icon'}>
            <ShoppingCart className='h-5 w-5' />
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <div className='flex flex-col justify-center items-center gap-2'>
            <ShoppingCart />
            <p>{t('checkout.cartEmpty')}</p>
            {unpaidRenewalOrders.length > 0 && (
              <div className='text-xs text-muted-foreground'>
                <p className='mb-1'>{t('checkout.unpaidRenewalOrders')}</p>
                <Link className='underline' to={ROUTES.checkout.amountsDue}>
                  {t('checkout.viewAmountsDue')}
                </Link>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={showCart} onOpenChange={() => setShowCart(!showCart)}>
      <PopoverTrigger asChild>
        <Button className='px-2' variant={'outline'} size={'default'}>
          <ShoppingCart className='h-5 w-5' />
          <span className='inline-flex items-center justify-center min-w-[20px] px-1 py-0.5 ms-2 text-xs font-semibold text-gray-800 bg-gray-200 rounded'>{itemsCount}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end'>
        <div className='flex flex-col items-end'>
          {cart.domains.map((domain, i) => (
            <CartBannerDomainItem key={i} cart={cart} domainItem={domain} />
          ))}
          {cart.staticHosting && <CartBannerStaticHostingItem cart={cart} staticHostingItem={cart.staticHosting} />}
          <Link to={'/checkout/cart'}>
            <Button className='mt-3' variant='outline' size='sm'>
              {t('checkout.goToCheckout')}
            </Button>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};
